import type { GlobalSearchResultItem, SearchResultsData } from 'models';
import { routes } from 'routes/routes';
import { Loading } from 'ui';

import { NoResultsItem } from '../../search-items';
import { SearchResultItem, searchResultHeaders } from '../../search-result-item';
import { type SearchItemTypeKey, getSearchResultPath } from '../../search-result-routes';
import { trackRecentSearch } from '../../services';
import { SearchResultsSection } from './search-results-section';

type P = {
  clearSearch: () => void;
  isLoading: boolean;
  onNavigate: (path: string) => void;
  searchResults: SearchResultsData;
  searchValue: string;
};

const LIMIT_CATEGORY_ITEMS = 3;

const searchCategories: SearchItemTypeKey[] = [
  'posts',
  'activities',
  'polls',
  'events',
  'communities',
  'organizations',
  'programs',
  'givings',
  'files',
  'users',
];

const searchItemApiType: Record<SearchItemTypeKey, string> = {
  activities: 'Activity',
  communities: 'Community',
  events: 'Event',
  files: 'Asset',
  givings: 'Giving',
  organizations: 'Organization',
  polls: 'Poll',
  posts: 'Post',
  programs: 'Program',
  users: 'User',
};

const SearchResultsPanel = ({ clearSearch, isLoading, onNavigate, searchResults, searchValue }: P) => {
  const categoriesWithItems = searchCategories.filter((type) => (searchResults[type]?.length ?? 0) > 0);

  const handleItemClick = (type: SearchItemTypeKey, item: GlobalSearchResultItem) => {
    clearSearch();
    onNavigate(getSearchResultPath(type, item));
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Defined by BE api
    void trackRecentSearch({ body: { recent_search: { id: item.id, type: searchItemApiType[type] } } });
  };

  const handleSeeAll = (type: SearchItemTypeKey) => {
    onNavigate(routes.search.results(type, searchValue));
  };

  if (isLoading) {
    return <Loading size="medium" />;
  }

  if (categoriesWithItems.length === 0) {
    return <NoResultsItem />;
  }

  return (
    <div className="global-search-input__panel">
      {categoriesWithItems.map((type) => {
        const items = searchResults[type] ?? [];
        const count = searchResults[`${type}Count`] ?? 0;

        return (
          <SearchResultsSection
            key={type}
            onSeeAll={() => {
              handleSeeAll(type);
            }}
            showSeeAll={count > LIMIT_CATEGORY_ITEMS}
            title={searchResultHeaders[type]}
          >
            {items.slice(0, LIMIT_CATEGORY_ITEMS).map((item) => (
              <SearchResultItem
                itemActionProps={{
                  onClick() {
                    handleItemClick(type, item);
                  },
                }}
                itemProps={item}
                key={item.id}
                type={type}
              />
            ))}
          </SearchResultsSection>
        );
      })}
    </div>
  );
};

export default SearchResultsPanel;
